import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useDepartments } from "@/contexts/DepartmentsContext";
import StockThumb from "@/components/StockThumb";
import {
  DollarSign,
  Save,
  Search,
  CheckCircle2,
  Package,
  MapPin,
  Loader2,
  Filter,
} from "lucide-react";

/**
 * QuickCosts — bulk-edit page for items that are missing a unit cost.
 *
 * Mirrors the "Cost & Reorder", "Pack content" and "Location" cards from
 * the full Edit Inventory dialog, but strips everything else so the WM
 * can motor through onboarding after a fresh reset (kitchen items, new
 * stock lines from Snap-to-Import, etc.).
 *
 * Every row uses the same "fill any one, others recalculate" pattern as
 * the full dialog: type Total purchase cost, Cost per base unit, or
 * Cost per pack sub-unit — the others update automatically.
 */

const pluralUnit = (n, unit) => {
  if (!unit) return "";
  if (Math.abs(Number(n)) === 1) return unit;
  return unit.endsWith("s") ? unit : `${unit}s`;
};

// ── Per-row editor ─────────────────────────────────────────────────────
const CostRow = ({ item, onSaved }) => {
  const [form, setForm] = useState({
    unit: item.unit || "unit",
    unit_cost: item.unit_cost ?? "",
    reorder_weeks: item.reorder_weeks ?? 4,
    pack_size: item.pack_size ?? "",
    pack_unit: item.pack_unit ?? "",
    bin_location: item.bin_location ?? "",
  });
  const [busy, setBusy] = useState(false);
  // Track which of the 3 cost fields is currently focused so recalc
  // doesn't yank the caret while the user is typing.
  const [costFocus, setCostFocus] = useState(null);
  const [costDraft, setCostDraft] = useState("");

  const set = (k, v) => setForm((prev) => ({ ...prev, [k]: v }));

  const qty = Number(item.current_qty) || 0;
  const cost = Number(form.unit_cost) || 0;
  const size = Number(form.pack_size) || 0;
  const unitLbl = form.unit || "unit";
  const hasPack = size > 0 && !!form.pack_unit;
  const total = cost * qty;
  const perSub = hasPack && size > 0 ? cost / size : 0;

  const setFromTotal = (v) => {
    const t = Number(v);
    if (qty > 0 && !Number.isNaN(t)) set("unit_cost", (t / qty).toFixed(4));
    else if (v === "") set("unit_cost", "");
  };
  const setFromPerPack = (v) => set("unit_cost", v);
  const setFromPerSub = (v) => {
    const s = Number(v);
    if (size > 0 && !Number.isNaN(s)) set("unit_cost", (s * size).toFixed(4));
    else if (v === "") set("unit_cost", "");
  };

  const displayTotal =
    costFocus === "total" ? costDraft : total > 0 ? total.toFixed(2) : "";
  const displayPerPack =
    costFocus === "perPack" ? costDraft : form.unit_cost ?? "";
  const displayPerSub =
    costFocus === "perSub" ? costDraft : perSub > 0 ? perSub.toFixed(4) : "";

  const onFocus = (which, initial) => () => {
    setCostFocus(which);
    setCostDraft(String(initial ?? ""));
  };
  const onBlur = () => {
    setCostFocus(null);
    setCostDraft("");
  };
  const onChange = (which, setter) => (e) => {
    setCostDraft(e.target.value);
    if (costFocus !== which) setCostFocus(which);
    setter(e.target.value);
  };

  const save = async () => {
    // Blank isn't a cost, but 0 legitimately is — some suppliers (DHL, Toll)
    // provide courier bags free of charge, so managers need to be able to
    // stamp the row as "free from supplier" and get it out of the queue.
    const raw = form.unit_cost;
    if (raw === "" || raw === null || raw === undefined || Number.isNaN(Number(raw))) {
      toast.error("Enter a cost (use 0 for free-from-supplier items)");
      return;
    }
    const parsed = Number(raw);
    if (parsed < 0) {
      toast.error("Cost can't be negative");
      return;
    }
    setBusy(true);
    try {
      const body = {
        unit: form.unit,
        unit_cost: parsed,
        cost_confirmed: true,
        reorder_weeks: Number(form.reorder_weeks) || 4,
        pack_size:
          form.pack_size !== "" && form.pack_size !== null
            ? Number(form.pack_size) || null
            : null,
        pack_unit: form.pack_unit ? String(form.pack_unit).trim() : null,
        bin_location: form.bin_location || null,
      };
      const { data } = await api.put(`/stock/${item.id}`, body);
      toast.success(`Saved cost for ${item.name}`);
      onSaved(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="nb-card p-4"
      data-testid={`qc-row-${item.id}`}
    >
      {/* Header — thumb + name + save */}
      <div className="flex items-center gap-3 mb-4">
        <StockThumb item={item} size="h-14 w-14" />
        <div className="flex-1 min-w-0">
          <div className="font-display text-lg leading-tight truncate">
            {item.name}
          </div>
          <div className="label-eyebrow text-[#607087] truncate">
            {[item.category, item.material, item.supplier_name]
              .filter(Boolean)
              .join(" · ")}
          </div>
          <div className="label-eyebrow mt-0.5">
            On hand:{" "}
            <span className="font-mono-num font-bold">
              {qty.toLocaleString()} {pluralUnit(qty, unitLbl)}
            </span>
          </div>
        </div>
        <button
          type="button"
          data-testid={`qc-save-${item.id}`}
          onClick={save}
          disabled={busy}
          className="nb-btn nb-btn-primary px-4 py-2 text-sm flex items-center gap-1 shrink-0"
        >
          {busy ? (
            <Loader2 size={14} className="animate-spin" />
          ) : (
            <Save size={14} strokeWidth={2.5} />
          )}
          Save
        </button>
      </div>

      {/* Card 1: Cost & Reorder — mirrors StockFormDialog */}
      <div className="nb-card-sm p-3 mb-3">
        <div className="flex items-center gap-2 mb-3">
          <DollarSign
            size={14}
            strokeWidth={2.5}
            className="text-[#228EBF]"
          />
          <h4 className="font-display text-sm tracking-wide">Cost & Reorder</h4>
        </div>

        {/* Base unit */}
        <label className="block mb-3">
          <div className="label-eyebrow mb-1">
            Base unit — what one item is called
          </div>
          <input
            data-testid={`qc-base-unit-${item.id}`}
            className="nb-input font-mono-num"
            list={`qc-unit-suggestions-${item.id}`}
            value={form.unit || ""}
            onChange={(e) => set("unit", e.target.value)}
            placeholder="e.g. bag, tub, sheet, roll, item"
          />
          <datalist id={`qc-unit-suggestions-${item.id}`}>
            <option value="bag" />
            <option value="box" />
            <option value="tub" />
            <option value="roll" />
            <option value="sheet" />
            <option value="item" />
            <option value="pair" />
            <option value="unit" />
            <option value="tube" />
            <option value="bottle" />
          </datalist>
          <div className="label-eyebrow mt-1 text-[#607087]">
            Every count, cost and stock number below is per one{" "}
            <b>{unitLbl}</b>.
          </div>
        </label>

        <div className="label-eyebrow mb-2 text-[#607087]">
          Fill <strong>any one</strong> — the others recalculate
        </div>
        <div
          className={`grid gap-3 ${hasPack ? "sm:grid-cols-3" : "sm:grid-cols-2"}`}
        >
          <label>
            <div className="label-eyebrow mb-1">Total purchase cost</div>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 font-mono-num text-[#607087]">
                $
              </span>
              <input
                data-testid={`qc-total-cost-${item.id}`}
                type="number"
                step="0.01"
                className="nb-input font-mono-num pl-7"
                value={displayTotal}
                onFocus={onFocus("total", total > 0 ? total.toFixed(2) : "")}
                onBlur={onBlur}
                onChange={onChange("total", setFromTotal)}
                placeholder="0.00"
                disabled={qty <= 0}
              />
            </div>
            <div className="label-eyebrow mt-1 text-[#607087]">
              for {qty || 0} {pluralUnit(qty, unitLbl)} on hand
            </div>
          </label>
          <label>
            <div className="label-eyebrow mb-1">Cost per {unitLbl}</div>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 font-mono-num text-[#607087]">
                $
              </span>
              <input
                data-testid={`qc-unit-cost-${item.id}`}
                type="number"
                step="0.01"
                className="nb-input font-mono-num pl-7"
                value={displayPerPack}
                onFocus={onFocus("perPack", form.unit_cost ?? "")}
                onBlur={onBlur}
                onChange={onChange("perPack", setFromPerPack)}
                placeholder="0.00"
              />
            </div>
            <div className="label-eyebrow mt-1 text-[#607087]">
              price of one {unitLbl}
            </div>
          </label>
          {hasPack && (
            <label>
              <div className="label-eyebrow mb-1">
                Cost per {form.pack_unit}
              </div>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 font-mono-num text-[#607087]">
                  $
                </span>
                <input
                  data-testid={`qc-per-sub-cost-${item.id}`}
                  type="number"
                  step="0.0001"
                  className="nb-input font-mono-num pl-7"
                  value={displayPerSub}
                  onFocus={onFocus("perSub", perSub > 0 ? perSub.toFixed(4) : "")}
                  onBlur={onBlur}
                  onChange={onChange("perSub", setFromPerSub)}
                  placeholder="0.0000"
                />
              </div>
              <div className="label-eyebrow mt-1 text-[#607087]">
                each {unitLbl} contains {size} {pluralUnit(size, form.pack_unit)}
              </div>
            </label>
          )}
        </div>

        {/* Live value chip */}
        {cost > 0 && (
          <div
            data-testid={`qc-summary-${item.id}`}
            className="mt-3 nb-card-sm p-2 bg-[#EAF5FB] text-xs"
          >
            <div className="font-mono-num">
              Stock value on hand:{" "}
              <strong>
                $
                {total.toLocaleString(undefined, {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
              </strong>
              {hasPack &&
                ` · ${(qty * size).toLocaleString()} ${pluralUnit(
                  qty * size,
                  form.pack_unit,
                )} total at $${perSub.toFixed(4)}/${form.pack_unit}`}
            </div>
          </div>
        )}

        <div className="mt-3">
          <label>
            <div className="label-eyebrow mb-1">Reorder window (weeks)</div>
            <input
              data-testid={`qc-reorder-weeks-${item.id}`}
              type="number"
              step="0.5"
              className="nb-input font-mono-num w-32"
              value={form.reorder_weeks}
              onChange={(e) => set("reorder_weeks", e.target.value)}
            />
          </label>
        </div>
      </div>

      {/* Card 2: Pack content — optional */}
      <div className="nb-card-sm p-3 mb-3 bg-[#FFF7E0]">
        <div className="flex items-center gap-2 mb-2">
          <Package size={14} strokeWidth={2.5} className="text-[#9A6F00]" />
          <h4 className="label-eyebrow text-[#9A6F00]">
            Pack content — optional
          </h4>
        </div>
        <div className="text-xs text-[#5C4A0F] mb-2">
          Only fill this if <b>each {unitLbl}</b> contains multiple sub-units
          you want to track (e.g., a 25 kg tub of glue, or a bag of 50 pads).
          Leave blank if you count individual pieces.
        </div>
        <div className="text-xs text-[#5C4A0F] font-display mb-2">
          Each 1 <b>{unitLbl}</b> contains…
        </div>
        <div className="grid grid-cols-2 gap-2">
          <label>
            <div className="label-eyebrow mb-1">How many?</div>
            <input
              data-testid={`qc-pack-size-${item.id}`}
              type="number"
              step="0.1"
              className="nb-input font-mono-num"
              value={form.pack_size ?? ""}
              onChange={(e) => set("pack_size", e.target.value)}
              placeholder="e.g. 25"
            />
          </label>
          <label>
            <div className="label-eyebrow mb-1">Of what?</div>
            <input
              data-testid={`qc-pack-unit-${item.id}`}
              className="nb-input"
              list={`qc-pack-unit-suggestions-${item.id}`}
              value={form.pack_unit ?? ""}
              onChange={(e) => set("pack_unit", e.target.value)}
              placeholder="kg / L / pcs / items"
            />
            <datalist id={`qc-pack-unit-suggestions-${item.id}`}>
              <option value="kg" />
              <option value="g" />
              <option value="L" />
              <option value="ml" />
              <option value="m" />
              <option value="cm" />
              <option value="pcs" />
              <option value="items" />
              <option value="pairs" />
              <option value="sheets" />
            </datalist>
          </label>
        </div>
      </div>

      {/* Card 3: Location */}
      <div className="nb-card-sm p-3">
        <div className="flex items-center gap-2 mb-2">
          <MapPin size={14} strokeWidth={2.5} className="text-[#228EBF]" />
          <h4 className="font-display text-sm tracking-wide">Location</h4>
        </div>
        <label>
          <div className="label-eyebrow mb-1">Bin / shelf location</div>
          <input
            data-testid={`qc-bin-${item.id}`}
            className="nb-input"
            value={form.bin_location || ""}
            onChange={(e) => set("bin_location", e.target.value)}
            placeholder="A1-03 · Rack 4 · Mezzanine bin 7"
          />
        </label>
      </div>
    </div>
  );
};

// ── Page shell ─────────────────────────────────────────────────────────
// A row is "missing a cost" only if it's never been confirmed by a manager.
// Once someone saves it — even at $0 for free-from-supplier lines like DHL
// courier bags — cost_confirmed flips to true and the row leaves the queue.
const hasNoCost = (it) => {
  if (it.cost_confirmed) return false;
  return !Number(it.unit_cost) || Number(it.unit_cost) <= 0;
};

const QuickCosts = () => {
  const { departments } = useDepartments();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [deptFilter, setDeptFilter] = useState("all");
  const [showAll, setShowAll] = useState(false);

  const load = () => {
    setLoading(true);
    api
      .get("/stock")
      .then((r) => setItems(r.data || []))
      .catch(() => toast.error("Could not load inventory"))
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  // When a row saves, splice its updated record back in place — cheaper
  // than a full reload and keeps scroll position steady.
  const onSaved = (updated) => {
    setItems((prev) =>
      prev.map((it) => (it.id === updated.id ? { ...it, ...updated } : it)),
    );
  };

  const missingCount = useMemo(
    () => items.filter(hasNoCost).length,
    [items],
  );

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return items
      .filter((it) => (showAll ? true : hasNoCost(it)))
      .filter((it) => {
        if (deptFilter === "all") return true;
        const list = it.departments?.length
          ? it.departments
          : it.department
            ? [it.department]
            : [];
        return list.includes(deptFilter);
      })
      .filter((it) => {
        if (!q) return true;
        return `${it.name} ${it.category || ""} ${it.material || ""} ${
          it.supplier_name || ""
        }`
          .toLowerCase()
          .includes(q);
      })
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [items, search, deptFilter, showAll]);

  return (
    <div className="p-4 md:p-6 lg:p-8" data-testid="quick-costs-page">
      <div className="flex items-center gap-3 flex-wrap mb-1">
        <DollarSign size={26} strokeWidth={2.5} className="text-[#228EBF]" />
        <h1 className="font-display text-4xl sm:text-5xl">Quick Costs</h1>
        <div
          className="ml-auto flex items-center gap-2 nb-card-sm px-3 py-1.5"
          data-testid="qc-missing-count"
        >
          {missingCount === 0 ? (
            <>
              <CheckCircle2
                size={16}
                strokeWidth={2.5}
                className="text-[#4DA86A]"
              />
              <span className="font-display text-sm">
                Every item has a cost 🎉
              </span>
            </>
          ) : (
            <span className="font-display text-sm">
              <span className="font-mono-num font-bold text-lg mr-1">
                {missingCount}
              </span>
              item{missingCount === 1 ? "" : "s"} still missing a cost
            </span>
          )}
        </div>
      </div>
      <p className="text-[#607087] text-sm mb-6 max-w-2xl">
        Bulk-fill costs, reorder windows, pack contents and bin locations for
        every item that doesn&apos;t have a cost yet. Tap any row&apos;s Save
        button as you go — the counter above updates live. Item free from the
        supplier (e.g. DHL/Toll courier bags)? Enter <b>0</b> and hit Save —
        it&apos;ll stamp the row as confirmed and drop it from the queue.
      </p>

      {/* Filter row */}
      <div className="nb-card p-3 mb-4 flex flex-col gap-2">
        <div className="flex flex-col md:flex-row gap-2">
          <div className="relative flex-1">
            <Search
              size={16}
              strokeWidth={2.5}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-[#607087]"
            />
            <input
              data-testid="qc-search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by name, category, material or supplier…"
              className="nb-input pl-9"
            />
          </div>
          <button
            type="button"
            data-testid="qc-toggle-show-all"
            onClick={() => setShowAll((v) => !v)}
            className={`nb-btn px-3 py-2 text-xs flex items-center gap-1 ${
              showAll ? "nb-btn-dark" : "bg-white"
            }`}
            title={
              showAll
                ? "Hide items that already have a cost"
                : "Show every item, even those with costs already set"
            }
          >
            <Filter size={12} strokeWidth={2.75} />
            {showAll ? "Showing all items" : "Missing costs only"}
          </button>
        </div>
        <div
          className="flex gap-1.5 flex-wrap items-center"
          data-testid="qc-dept-chips"
        >
          <span className="text-[10px] font-bold uppercase tracking-wider text-[#607087] mr-1">
            Department
          </span>
          <button
            type="button"
            onClick={() => setDeptFilter("all")}
            data-testid="qc-dept-all"
            className={`nb-btn px-3 py-1.5 text-xs ${
              deptFilter === "all" ? "nb-btn-dark" : "bg-white"
            }`}
          >
            All depts
          </button>
          {(departments || []).map((d) => (
            <button
              key={d.id}
              type="button"
              onClick={() => setDeptFilter(d.name)}
              data-testid={`qc-dept-${d.name}`}
              className={`nb-btn px-3 py-1.5 text-xs ${
                deptFilter === d.name ? "nb-btn-dark" : "bg-white"
              }`}
            >
              {d.name}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="text-center py-16 text-[#607087]">
          <Loader2 className="inline-block animate-spin mr-2" size={20} />
          Loading inventory…
        </div>
      ) : rows.length === 0 ? (
        <div
          className="nb-card p-8 text-center"
          data-testid="qc-empty-state"
        >
          <CheckCircle2
            size={48}
            strokeWidth={2.5}
            className="mx-auto mb-3 text-[#4DA86A]"
          />
          <div className="font-display text-xl mb-1">
            {showAll ? "No matching items" : "All costed — nice work!"}
          </div>
          <div className="text-sm text-[#607087]">
            {showAll
              ? "Adjust the search or department filter to see more rows."
              : "Nothing left to fill in this view. Toggle 'Showing all items' if you want to edit costs that are already set."}
          </div>
        </div>
      ) : (
        <div
          className="grid grid-cols-1 xl:grid-cols-2 gap-4"
          data-testid="qc-rows"
        >
          {rows.map((it) => (
            <CostRow key={it.id} item={it} onSaved={onSaved} />
          ))}
        </div>
      )}
    </div>
  );
};

export default QuickCosts;
